import { useState, useRef, useEffect } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2 } from "lucide-react";

export default function AudioPlayer({ src, title, onEnded }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => setDuration(audio.duration);
    const onEnd = () => { setPlaying(false); onEnded?.(); };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", onEnd);
    };
  }, [src, onEnded]);

  const togglePlay = () => {
    if (playing) audioRef.current?.pause();
    else audioRef.current?.play();
    setPlaying(!playing);
  };

  const seek = (e) => {
    const t = parseFloat(e.target.value);
    if (audioRef.current) audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const skip = (sec) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, Math.max(0, audioRef.current.currentTime + sec));
    }
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4" data-testid="audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      {title && <p className="text-sm font-sans font-medium text-foreground mb-3 truncate">{title}</p>}
      <div className="flex items-center gap-3">
        <button onClick={() => skip(-10)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="audio-skip-back">
          <SkipBack className="h-4 w-4" />
        </button>
        <button
          onClick={togglePlay}
          className="w-10 h-10 flex items-center justify-center bg-[#1E88E5] hover:bg-[#1565C0] text-white transition-colors"
          data-testid="audio-play-pause"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>
        <button onClick={() => skip(10)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="audio-skip-forward">
          <SkipForward className="h-4 w-4" />
        </button>
        <span className="text-xs font-sans text-muted-foreground w-10 text-right">{fmt(currentTime)}</span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          value={currentTime}
          onChange={seek}
          className="audio-slider flex-1"
          data-testid="audio-progress"
        />
        <span className="text-xs font-sans text-muted-foreground w-10">{fmt(duration)}</span>
        <Volume2 className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  );
}
